import { RunSummaryDto } from '../../../backend/src/matter/subset/dto/run-summary.dto';
import { RunType } from '../../../backend/src/run/run-type';
import { RunStatus } from '../../../backend/src/types/types';
import { MatterRun, Run } from '@/api/types/backend-dto-types';
import { RunWithSubsetName } from '@/new/components/breadcrumb';
import { isProcessingStatus } from '@/new/lib/pipeline-status';

export const getRunType = (run: Run | RunSummaryDto | MatterRun, runOnAllDocsCase: boolean) => {
  switch (run.type) {
    case RunType.Regular:
      return 'Regular';
    case RunType.AddDocs:
      return 'New Documents';
    case RunType.AddDocsUpdateSynopsis:
      return 'New Documents & Updated Details';
    case RunType.UpdateSynopsis:
      return runOnAllDocsCase ? 'Updated Matter Issues' : 'Recontextualising Subset Facts';
    case RunType.UpdateInconsistencies:
      return 'Updated Inconsistencies';
    case RunType.DeleteDocuments:
      return 'Deleted Documents';
    case RunType.Reprocess:
      return 'Reprocessing Run';
    default:
      return 'Unknown';
  }
};

/**
 * Check if a run is ongoing (status is a processing status)
 */
export const isRunOngoing = (run: Run | RunWithSubsetName | MatterRun): boolean =>
  !!run.status && isProcessingStatus(run.status as RunStatus);

/**
 * Check if any run in an array is ongoing
 */
export const hasAnyOngoingRun = (runs: Run[] | RunWithSubsetName[] | MatterRun[] | undefined): boolean => {
  if (!runs || runs.length === 0) return false;
  return runs.some(isRunOngoing);
};

/**
 * Check if any runs are ongoing on the all docs case for a matter
 */
export const hasAnyOngoingRunOnAllDocsCase = (
  runs: Run[] | RunWithSubsetName[] | MatterRun[] | undefined,
  allDocsCaseId: string,
): boolean => {
  if (!runs || !Array.isArray(runs) || runs.length === 0) return false;
  return runs.some((run) => run.caseId === allDocsCaseId && isRunOngoing(run));
};

/**
 * Check if any runs are ongoing which modify the number of documents in the matter
 */
export const hasOngoingRunWhichModifiesDocumentCount = (
  runs: Run[] | RunWithSubsetName[] | MatterRun[] | undefined,
): boolean => {
  if (!runs || !Array.isArray(runs) || runs.length === 0) return false;
  return runs.some(
    (run) =>
      (run.type === RunType.AddDocs ||
        run.type === RunType.AddDocsUpdateSynopsis ||
        run.type === RunType.Regular ||
        run.type === RunType.DeleteDocuments) &&
      isRunOngoing(run),
  );
};
